import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Heart, Star, ShoppingCart, Package, AlertCircle } from 'lucide-react'
import { useCartStore, useWishlistStore } from '../../store/index.js'
import toast from 'react-hot-toast'

// ── Category emoji helper (mirrors customer frontend) ─────────────────────────
export function getCategoryEmoji(product) {
  const desc  = (product?.description || product?.name || '').toLowerCase()
  const brand = (product?.brand || '').toLowerCase()
  const cat   = (product?.category_name || product?.category || '').toLowerCase()
  const combined = `${cat} ${desc} ${brand}`

  if (/whiskey|whisky|bourbon|scotch/.test(cat))  return '🥃'
  if (/vodka/.test(cat))                           return '🍸'
  if (/\bgin\b/.test(cat))                         return '🌿'
  if (/tequila/.test(cat))                         return '🌵'
  if (/rum/.test(cat))                             return '🏝️'
  if (/brandy|cognac/.test(cat))                   return '🍂'
  if (/liqueur/.test(cat))                         return '✨'
  if (/aperitif/.test(cat))                        return '🫗'
  if (/cane/.test(cat))                            return '🌾'
  if (/shooter/.test(cat))                         return '🔫'
  if (/champagne|sparkling/.test(cat))             return '🥂'
  if (/port|sherry|muscadel/.test(cat))            return '🫙'
  if (/box wine/.test(cat))                        return '📦'
  if (/wine/.test(cat))                            return '🍷'
  if (/craft beer/.test(cat))                      return '🍻'
  if (/cider/.test(cat))                           return '🍋'
  if (/rtd cocktail/.test(cat))                    return '🍹'
  if (/rtd/.test(cat))                             return '🥤'
  if (/beer/.test(cat))                            return '🍺'
  if (/energy/.test(cat))                          return '⚡'
  if (/juice/.test(cat))                           return '🍊'
  if (/cordial/.test(cat))                         return '🍓'
  if (/water/.test(cat))                           return '🫧'
  if (/mineral/.test(cat))                         return '💧'
  if (/tobacco|cigarette/.test(cat))               return '🚬'
  if (/spirits/.test(cat))                         return '🥃'
  if (/glassware|glass/.test(cat))                 return '🥂'
  if (/snack/.test(cat))                           return '🍿'
  if (/gift/.test(cat))                            return '🎁'

  // description/brand fallback
  if (/whiskey|whisky|bourbon|scotch/.test(combined)) return '🥃'
  if (/vodka/.test(combined))                         return '🍸'
  if (/\bgin\b/.test(combined))                       return '🌿'
  if (/tequila|mezcal/.test(combined))                return '🌵'
  if (/\brum\b/.test(combined))                       return '🏝️'
  if (/brandy|cognac/.test(combined))                 return '🍂'
  if (/liqueur|amarula|baileys|kahlua/.test(combined)) return '✨'
  if (/champagne|sparkling|prosecco/.test(combined))  return '🥂'
  if (/port|sherry|muscadel/.test(combined))          return '🫙'
  if (/wine/.test(combined))                          return '🍷'
  if (/craft beer/.test(combined))                    return '🍻'
  if (/cider/.test(combined))                         return '🍋'
  if (/\brtd\b/.test(combined))                       return '🥤'
  if (/beer|lager|ale|stout/.test(combined))          return '🍺'
  if (/energy drink|red bull|monster/.test(combined)) return '⚡'
  if (/juice|nectar/.test(combined))                  return '🍊'
  if (/water/.test(combined))                         return '🫧'
  if (/glass|glassware/.test(combined))               return '🥂'
  if (/snack|chip|crisp|nut/.test(combined))          return '🍿'
  if (/gift|hamper/.test(combined))                   return '🎁'
  return '🍾'
}

// Product Card - compact grid version
export function ProductCard({ product, onClick }) {
  const navigate = useNavigate()
  const addItem = useCartStore(s => s.addItem)
  const cartItems = useCartStore(s => s.items)
  const { toggle, has } = useWishlistStore()
  const isWished = has(product.id)
  const cartItem = cartItems.find(i => i.id === product.id)
  const isInCart = !!cartItem
  const cartQty  = cartItem?.qty ?? 0
  const [qty, setQty] = useState(1)
  const [qtyDraft, setQtyDraft] = useState('1')

  // Keep draft in sync when qty changes via +/- buttons
  useEffect(() => { setQtyDraft(String(qty)) }, [qty])

  const handleAddToCart = (e) => {
    e.stopPropagation()
    if (!product.inStock) return
    addItem(product, qty)
    toast.success(`${qty}× added to cart`, { duration: 1500 })
    setQty(1)
    setQtyDraft('1')
  }

  const handleWishlist = (e) => {
    e.stopPropagation()
    toggle(product)
    toast.success(isWished ? 'Removed from wishlist' : 'Added to wishlist', { duration: 1500 })
  }

  const dec = (e) => { e.stopPropagation(); setQty(q => Math.max(1, q - 1)) }
  const inc = (e) => { e.stopPropagation(); setQty(q => q + 1) }

  const commitQty = () => {
    const n = parseInt(qtyDraft, 10)
    if (!isNaN(n) && n >= 1) {
      setQty(Math.max(1, Math.min(999, n)))
    } else {
      setQtyDraft(String(qty)) // revert
    }
  }

  return (
    <div className="product-card" onClick={onClick}>
      {/* Image — relative wrapper so overlays are never clipped by overflow-hidden */}
      <div className="relative aspect-square bg-surface-elevated overflow-hidden">
        <ProductImage product={product} className="w-full h-full object-contain p-1" />

        {product.badge && (
          <span className={`absolute top-2 left-2 badge text-white text-[10px] ${
            product.badge === 'Out of Stock' ? 'bg-gray-600' :
            product.badge === 'Luxury' ? 'bg-gold text-gray-900' :
            product.badge === 'On Sale' ? 'bg-green-700' : 'bg-primary'
          }`}>
            {product.badge}
          </span>
        )}

        {/* Cart indicator top-left */}
        {/* In cart: teal, tappable → navigates to /cart to manage */}
        {/* Not in cart: dim grey, non-interactive */}
        {!product.badge && (
          isInCart ? (
            <button
              onClick={e => { e.stopPropagation(); navigate('/cart') }}
              className="absolute top-2 left-2 w-7 h-7 rounded-full bg-primary/90 backdrop-blur-sm flex items-center justify-center z-10 transition-colors active:bg-primary"
              title={`In your cart (×${cartQty}) — tap to manage`}
            >
              <ShoppingCart size={13} className="text-white" />
              <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-0.5 rounded-full bg-gold text-gray-900 text-[9px] font-bold font-display flex items-center justify-center leading-none shadow-sm">
                {cartQty > 99 ? '99+' : cartQty}
              </span>
            </button>
          ) : (
            <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-surface-dark/70 backdrop-blur-sm flex items-center justify-center z-10">
              <ShoppingCart size={13} className="text-gray-500" />
            </div>
          )
        )}
      </div>
      {/* Heart button — always visible; teal background when wishlisted */}
      <button
        onClick={handleWishlist}
        className={`absolute top-2 right-2 w-7 h-7 rounded-full backdrop-blur-sm flex items-center justify-center z-10 transition-colors ${
          isWished ? 'bg-primary/90' : 'bg-surface-dark/70'
        }`}
      >
        <Heart size={14} className={isWished ? 'fill-white text-white' : 'text-gray-400'} />
      </button>

      {/* Info */}
      <div className="p-2.5">
        <p className="text-[10px] text-gray-500 font-display mb-0.5">
          {getCategoryEmoji(product)} {product.category_name || product.category}
        </p>
        <p className="text-sm text-white font-display font-medium leading-tight line-clamp-2 mb-2">
          {product.name}
        </p>
        <p className="price text-sm mb-2">R{product.price.toFixed(2)}</p>

        {/* Qty stepper + Add to cart — always visible */}
        <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
          <div className="flex items-center bg-surface-elevated rounded-lg border border-surface-border overflow-hidden flex-shrink-0">
            <button onClick={dec} disabled={qty <= 1}
              className="w-7 h-7 flex items-center justify-center text-gray-300 active:bg-surface-border disabled:text-gray-600 text-base leading-none transition-colors">−</button>
            <input
              type="number"
              min={1}
              max={999}
              value={qtyDraft}
              onChange={e => { e.stopPropagation(); setQtyDraft(e.target.value) }}
              onBlur={commitQty}
              onKeyDown={e => {
                e.stopPropagation()
                if (e.key === 'Enter')  { commitQty(); e.target.blur() }
                if (e.key === 'Escape') { setQtyDraft(String(qty)); e.target.blur() }
              }}
              onClick={e => { e.stopPropagation(); e.target.select() }}
              className="w-8 text-center text-xs font-display font-semibold text-white bg-transparent focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <button onClick={inc}
              className="w-7 h-7 flex items-center justify-center text-gray-300 active:bg-surface-border text-base leading-none transition-colors">+</button>
          </div>
          <button
            onClick={handleAddToCart}
            disabled={!product.inStock}
            className={`flex-1 h-7 rounded-lg flex items-center justify-center gap-1 text-[11px] font-display font-semibold transition-colors ${
              product.inStock ? 'bg-primary active:bg-primary-dark text-white' : 'bg-surface-border text-gray-500 cursor-not-allowed'
            }`}
          >
            <ShoppingCart size={12} />
            {product.inStock ? 'Add' : 'N/A'}
          </button>
        </div>
      </div>
    </div>
  )
}

// Product Card - horizontal list version
export function ProductCardRow({ product, onClick }) {
  const addItem = useCartStore(s => s.addItem)
  const { toggle, has } = useWishlistStore()
  const isWished = has(product.id)

  return (
    <div
      className="flex gap-3 bg-surface-card rounded-2xl border border-surface-border p-3 active:scale-[0.98] transition-transform duration-150 cursor-pointer"
      onClick={onClick}
    >
      <div className="w-20 h-20 bg-surface-elevated rounded-xl flex-shrink-0 overflow-hidden">
        <ProductImage product={product} className="w-full h-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] text-gray-500 font-display">{product.category}</p>
        <p className="text-sm text-white font-display font-medium line-clamp-2 mt-0.5">{product.name}</p>
        <div className="flex items-center gap-1 mt-1">
          <Star size={11} className="fill-gold text-gold" />
          <span className="text-[11px] text-gray-400 font-display">{product.rating} ({product.reviews})</span>
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <span className="price text-sm">R{product.price.toFixed(2)}</span>
          <div className="flex gap-1.5">
            <button
              onClick={(e) => { e.stopPropagation(); toggle(product) }}
              className="w-7 h-7 rounded-lg border border-surface-border flex items-center justify-center"
            >
              <Heart size={13} className={isWished ? 'fill-primary text-primary' : 'text-gray-400'} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); if (product.inStock) { addItem(product, 1); toast.success('Added!', { duration: 1500 }) } }}
              disabled={!product.inStock}
              className={`w-7 h-7 rounded-lg flex items-center justify-center ${product.inStock ? 'bg-primary' : 'bg-surface-border'}`}
            >
              <ShoppingCart size={13} className="text-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Skeleton loaders
export function ProductCardSkeleton() {
  return (
    <div className="bg-surface-card rounded-2xl border border-surface-border overflow-hidden">
      <div className="aspect-square shimmer" />
      <div className="p-2.5 space-y-2">
        <div className="h-2 w-12 shimmer rounded" />
        <div className="h-3 w-full shimmer rounded" />
        <div className="h-3 w-3/4 shimmer rounded" />
        <div className="h-4 w-16 shimmer rounded" />
      </div>
    </div>
  )
}

export function RowSkeleton() {
  return (
    <div className="flex gap-3 bg-surface-card rounded-2xl border border-surface-border p-3">
      <div className="w-20 h-20 shimmer rounded-xl flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-2 w-16 shimmer rounded" />
        <div className="h-3 w-full shimmer rounded" />
        <div className="h-3 w-2/3 shimmer rounded" />
        <div className="h-4 w-20 shimmer rounded" />
      </div>
    </div>
  )
}

// Product image with real image support + emoji fallback
export function ProductImage({ product, className }) {
  const [imgError, setImgError] = useState(false)
  const colors = {
    'Whisky': '#8B4513', 'Wine': '#722F37', 'Vodka': '#4169E1',
    'Beer': '#DAA520', 'Gin': '#228B22', 'Rum': '#8B6914',
    'Brandy': '#800020', 'Soft Drinks': '#FF6347'
  }
  const icons = { 'Whisky': '🥃', 'Wine': '🍷', 'Vodka': '🍸', 'Beer': '🍺', 'Gin': '🌿', 'Rum': '🏝️', 'Brandy': '🍇', 'Soft Drinks': '🥤' }
  const bg = colors[product?.category] || colors[product?.category_name] || '#0e947a'

  // Try real image first: primary_image → image → stock_code path.
  // Skip legacy "assets/…" placeholders (not in MappedData) so the category fallback shows.
  const primary = product?.primary_image && !String(product.primary_image).startsWith('assets/')
    ? product.primary_image : null
  const imageUrl = primary
    ? `/MappedData/${primary}`
    : product?.image
      ? `/MappedData/${product.image}`
      : product?.stock_code
        ? `/MappedData/${product.stock_code}/1.webp`
        : null

  if (imageUrl && !imgError) {
    return (
      <img
        src={imageUrl}
        alt={product?.name || 'Product'}
        onError={() => setImgError(true)}
        className={`${className} object-contain p-1`}
      />
    )
  }

  return (
    <div className={`${className} flex flex-col items-center justify-center`}
      style={{ background: `linear-gradient(135deg, ${bg}22, ${bg}44)` }}>
      <span className="text-4xl">{getCategoryEmoji(product)}</span>
    </div>
  )
}

// Empty state
export function EmptyState({ icon: Icon = Package, title, message, action, actionLabel }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
      <div className="w-16 h-16 rounded-2xl bg-surface-elevated flex items-center justify-center mb-4">
        <Icon size={28} className="text-gray-500" />
      </div>
      <h3 className="font-heading text-white text-lg mb-2">{title}</h3>
      <p className="text-gray-400 text-sm font-display mb-6">{message}</p>
      {action && (
        <button onClick={action} className="btn-primary">{actionLabel}</button>
      )}
    </div>
  )
}

// Quantity control
export function QtyControl({ qty, onIncrease, onDecrease, onSet, min = 1 }) {
  const [draft, setDraft] = useState(String(qty))

  // Keep draft in sync when qty changes externally (e.g. server sync)
  useEffect(() => { setDraft(String(qty)) }, [qty])

  const commit = () => {
    const n = parseInt(draft, 10)
    if (!isNaN(n) && n >= min && n !== qty) {
      onSet ? onSet(Math.min(999, Math.max(min, n))) : null
    } else {
      setDraft(String(qty)) // revert invalid input
    }
  }

  return (
    <div className="qty-control">
      <button onClick={onDecrease} className="qty-btn" disabled={qty <= min}>
        <span className="text-lg leading-none">−</span>
      </button>
      <input
        type="number"
        min={min}
        max={999}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') { commit(); e.target.blur() }
          if (e.key === 'Escape') { setDraft(String(qty)); e.target.blur() }
        }}
        onClick={e => e.target.select()}
        className="font-display font-semibold text-white w-10 text-center bg-transparent focus:outline-none focus:bg-surface-elevated rounded text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <button onClick={onIncrease} className="qty-btn">
        <span className="text-lg leading-none">+</span>
      </button>
    </div>
  )
}

// Status badge
export function StatusBadge({ status }) {
  const styles = {
    pending: 'bg-amber-500/20 text-amber-400',
    processing: 'bg-blue-500/20 text-blue-400',
    shipped: 'bg-purple-500/20 text-purple-400',
    delivered: 'bg-green-500/20 text-green-400',
    cancelled: 'bg-red-500/20 text-red-400',
  }
  return (
    <span className={`badge capitalize ${styles[status] || 'bg-gray-500/20 text-gray-400'}`}>
      {status}
    </span>
  )
}

// Offline banner
export function OfflineBanner() {
  return (
    <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-xl px-4 py-3 mx-4 mb-4">
      <AlertCircle size={16} className="text-amber-400 flex-shrink-0" />
      <span className="text-amber-400 text-sm font-display">You're offline. Showing cached data.</span>
    </div>
  )
}

// Section header
export function SectionHeader({ title, action, actionLabel }) {
  return (
    <div className="flex items-center justify-between px-4 mb-3">
      <h2 className="section-title">{title}</h2>
      {action && (
        <button onClick={action} className="text-primary text-sm font-display font-semibold">
          {actionLabel || 'See all'}
        </button>
      )}
    </div>
  )
}
